import { Clock, Languages } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatGuideExperience, guideSpecialtyLabels, type GuideType } from '@/types/types_guide';
import { liveGuideAvailability, type GuideDiaryType } from '@/hooks/useGuideAvailability';

interface GuideCardProps {
  guide: GuideType;
  /** This guide's diary, or undefined while it loads or if they have no portal link. */
  diary: GuideDiaryType | undefined;
  /** Today, `yyyy-mm-dd` — passed in so a grid of cards agrees on what "today" is. */
  today: string;
  onOpen: () => void;
}

/** A single tour leader on the public roster grid. */
export function GuideCard({ guide, diary, today, onOpen }: GuideCardProps) {
  // Undefined diary means we could not ask (no portal link, /portal does not
  // know them, or the lookup failed), which is not the same as "booked" — the
  // dot is left grey and says so rather than claiming the guide is busy.
  const freeToday = diary ? diary.has(today) : undefined;

  return (
    <div className='tw:group tw:flex tw:flex-col tw:overflow-hidden tw:border tw:border-ed-black/10 tw:bg-white tw:transition-shadow tw:hover:shadow-xl'>
      <div className='tw:relative tw:aspect-[4/3] tw:overflow-hidden tw:bg-ed-cream'>
        {guide.photo ? (
          <img
            src={guide.photo}
            alt={guide.name}
            loading='lazy'
            className='tw:h-full tw:w-full tw:object-cover tw:transition-transform tw:duration-700 tw:group-hover:scale-105'
          />
        ) : (
          <div className='tw:flex tw:h-full tw:w-full tw:items-center tw:justify-center tw:font-serif tw:text-5xl tw:text-ed-umber/30'>
            {guide.name.slice(0, 1)}
          </div>
        )}

        {guide.specialties.length > 0 && (
          <div className='tw:absolute tw:right-4 tw:bottom-4 tw:left-4 tw:flex tw:flex-wrap tw:gap-2'>
            {guide.specialties.slice(0, 3).map((s) => (
              <span
                key={s}
                className='tw:rounded-full tw:bg-ed-black/70 tw:px-3 tw:py-1 tw:text-xs tw:text-ed-cream tw:backdrop-blur-sm'
              >
                {guideSpecialtyLabels[s]}
              </span>
            ))}
          </div>
        )}
      </div>

      <div className='tw:flex tw:flex-1 tw:flex-col tw:p-6'>
        <div className='tw:flex tw:items-start tw:justify-between tw:gap-3'>
          <div>
            <h3 className='tw:font-serif tw:text-xl tw:text-ed-black'>{guide.name}</h3>
            {guide.nameJp && <p className='tw:text-sm tw:text-ed-stone'>{guide.nameJp}</p>}
          </div>
          {/* Dropped entirely while diaries are switched off: the dot earns its
              place by telling one card apart from another, and a grid where
              every one of them is grey says nothing at all. */}
          {liveGuideAvailability && (
            <span
              title={
                freeToday === undefined
                  ? 'Availability on request'
                  : freeToday
                    ? 'Free today'
                    : 'Not available today'
              }
              className={cn(
                'tw:mt-1.5 tw:inline-block tw:size-2 tw:shrink-0 tw:rounded-full',
                freeToday === undefined ? 'tw:bg-ed-stone/40' : freeToday ? 'tw:bg-ed-green' : 'tw:bg-ed-red',
              )}
            />
          )}
        </div>

        {guide.bio && (
          <p className='tw:mt-3 tw:line-clamp-2 tw:flex-1 tw:text-sm tw:leading-relaxed tw:text-ed-charcoal/80'>
            {guide.bio}
          </p>
        )}

        <div className='tw:mt-4 tw:flex tw:flex-wrap tw:items-center tw:gap-4 tw:text-xs tw:text-ed-stone'>
          {guide.languages.length > 0 && (
            <span className='tw:flex tw:items-center tw:gap-1.5'>
              <Languages className='tw:size-4' />
              {guide.languages.join(', ')}
            </span>
          )}
          {guide.guidingSince && (
            <span className='tw:flex tw:items-center tw:gap-1.5'>
              <Clock className='tw:size-4' />
              {formatGuideExperience(guide.guidingSince)}
            </span>
          )}
        </div>

        <button
          type='button'
          onClick={onOpen}
          className='tw:mt-5 tw:w-full tw:border tw:border-ed-gold tw:py-3 tw:text-sm tw:font-medium tw:text-ed-gold tw:transition-all tw:duration-300 tw:hover:bg-ed-gold tw:hover:text-ed-black'
        >
          View Profile &amp; Availability
        </button>
      </div>
    </div>
  );
}
